import './bottom-nav.css'

import { useModal } from '@ebay/nice-modal-react'
import { Link, useNavigate } from '@tanstack/router'
import { type ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LazyLoadImage } from 'react-lazy-load-image-component'

import best_odds from '../../assets/common/best_odds.webp'
import casino_dark_after from '../../assets/common/casino_active.webp'
import casino_light_after from '../../assets/common/casino_light_active.webp'
import casino_light_before from '../../assets/common/casino_light_off.webp'
import casino_dark_before from '../../assets/common/casino_off.webp'
import deportes_1_light_after from '../../assets/common/deportes_1_light_active.webp'
import deportes_1_light_before from '../../assets/common/deportes_1_light_off.webp'
import deportes_2_light_after from '../../assets/common/deportes_2_light_active.webp'
import deportes_2_light_before from '../../assets/common/deportes_2_light_off.webp'
import deportes_dark_after from '../../assets/common/deportes_active.webp'
import deportes_dark_before from '../../assets/common/deportes_off.webp'
import maximize_light from '../../assets/common/expand_active.webp'
import minimize_light from '../../assets/common/expand_off.webp'
import favoritos_dark_after from '../../assets/common/favoritos_active.webp'
import favoritos_light_after from '../../assets/common/favoritos_light_active.webp'
import favoritos_light_before from '../../assets/common/favoritos_light_off.webp'
import favoritos_dark_before from '../../assets/common/favoritos_off.webp'
import maximize_dark from '../../assets/common/maximize.webp'
import minimize_dark from '../../assets/common/minimize.webp'
import more_live from '../../assets/common/more_live.webp'
import recently_dark_after from '../../assets/common/recently_active.webp'
import recently_light_after from '../../assets/common/recently_light_active.webp'
import recently_light_before from '../../assets/common/recently_light_off.webp'
import recently_dark_before from '../../assets/common/recently_off.webp'
import referral_dark_after from '../../assets/common/referral_active.webp'
import referral_light_after from '../../assets/common/referral_light_active.webp'
import referral_light_before from '../../assets/common/referral_light_off.webp'
import referral_dark_before from '../../assets/common/referral_off.webp'
import sponsors_dark_after from '../../assets/common/sponsors_active.webp'
import sponsors_dark_before from '../../assets/common/sponsors_off.webp'
import sponsors_light_after from '../../assets/common/sponsors_light_active.webp'
import sponsors_light_before from '../../assets/common/sponsors_light_off.webp'
import { countryIncludes } from '../../constants/countries'
import useGames from '../../hooks/useGames'
import { useBoundStore } from '../../store/store'
import classNames from '../../utils/classNames'
import SearchModal from '../casino/search-modal'

type TabObj = {
  icon: ReactNode
  badge?: ReactNode
  label: string
  link: string
  onLinkClick: undefined | (() => void)
  isProtected: boolean
}

const BottomNav = () => {
  const theme = useBoundStore(state => state.common.theme)
  const username = useBoundStore(state => state.user.username)
  const isFullScreen = useBoundStore(state => state.common.isFullScreen)
  const setIsFullScreen = useBoundStore(state => state.common.setIsFullScreen)
  const { loadMoreGames } = useGames()
  const navigate = useNavigate()
  const searchModal = useModal(SearchModal)
  const { t } = useTranslation(['drawer', 'casino'])
  const fullPath = location.pathname + location.search
  const hasSportsTwo = countryIncludes(['AR', 'CL', 'EC', 'PY'])

  const {
    minimize,
    maximize,
    deportes_1_after,
    deportes_1_before,
    deportes_2_after,
    deportes_2_before,
    casino_after,
    casino_before,
    favoritos_after,
    favoritos_before,
    recently_after,
    recently_before,
    referral_after,
    referral_before,
  } = useMemo(() => {
    return {
      minimize: theme === 'dark' ? minimize_dark : minimize_light,
      maximize: theme === 'dark' ? maximize_dark : maximize_light,
      deportes_1_after:
        theme === 'dark' ? deportes_dark_after : deportes_1_light_after,
      deportes_1_before:
        theme === 'dark' ? deportes_dark_before : deportes_1_light_before,
      deportes_2_after:
        theme === 'dark' ? deportes_dark_after : deportes_2_light_after,
      deportes_2_before:
        theme === 'dark' ? deportes_dark_before : deportes_2_light_before,
      casino_after: theme === 'dark' ? casino_dark_after : casino_light_after,
      casino_before:
        theme === 'dark' ? casino_dark_before : casino_light_before,
      favoritos_after:
        theme === 'dark' ? favoritos_dark_after : favoritos_light_after,
      favoritos_before:
        theme === 'dark' ? favoritos_dark_before : favoritos_light_before,
      recently_after:
        theme === 'dark' ? recently_dark_after : recently_light_after,
      recently_before:
        theme === 'dark' ? recently_dark_before : recently_light_before,
      referral_after:
        theme === 'dark' ? referral_dark_after : referral_light_after,
      referral_before:
        theme === 'dark' ? referral_dark_before : referral_light_before,
    }
  }, [theme])

  const commonTabs: TabObj[] = [
    {
      icon: (
        <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
          <LazyLoadImage
            className="icon-after opacity-0 absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Referral"
            src={referral_after}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
          <LazyLoadImage
            className="icon-before opacity-1 absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Referral"
            src={referral_before}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
        </div>
      ),
      label: t('invite'),
      link: '/profile/referral',
      onLinkClick: undefined,
      isProtected: true,
    },
    {
      icon: (
        <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
          <LazyLoadImage
            className="icon-after opacity-0 absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino Favoritos"
            src={favoritos_after}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
          <LazyLoadImage
            className="icon-before opacity-1 absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino Favoritos"
            src={favoritos_before}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
        </div>
      ),
      label: t('casino:favorites'),
      link: '/casino/favoritos',
      onLinkClick: undefined,
      isProtected: true,
    },
    {
      icon: (
        <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
          <LazyLoadImage
            className="icon-after opacity-0 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Deportes"
            src={deportes_1_after}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
          <LazyLoadImage
            className="icon-before opacity-1 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Deportes"
            src={deportes_1_before}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
        </div>
      ),
      label: t('sports'),
      link: hasSportsTwo && !username ? '/sports/bti' : '/sports/im',
      onLinkClick: undefined,
      isProtected: false,
    },
    {
      icon: (
        <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
          <LazyLoadImage
            className="icon-after opacity-0 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino"
            src={casino_after}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
          <LazyLoadImage
            className="icon-before opacity-1 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino"
            src={casino_before}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
        </div>
      ),
      label: t('casino'),
      link: `/casino/inicio`,
      onLinkClick: () => {
        loadMoreGames('15648', '', true)
      },
      isProtected: false,
    },
  ]

  const sportTabs: TabObj[] = [
    {
      icon: (
        <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
          <LazyLoadImage
            className="icon-after opacity-0 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino"
            src={casino_after}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
          <LazyLoadImage
            className="icon-before opacity-1 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino"
            src={casino_before}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
        </div>
      ),
      label: t('casino'),
      link: `/casino/inicio`,
      onLinkClick: undefined,
      isProtected: false,
    },
    {
      icon: (
        <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
          <LazyLoadImage
            className="icon-after opacity-0 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Deportes 1"
            src={deportes_1_after}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
          <LazyLoadImage
            className="icon-before opacity-1 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Deportes 1"
            src={deportes_1_before}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
        </div>
      ),
      label: hasSportsTwo ? `${t('sports')} 1` : t('sports'),
      link: '/sports/im',
      onLinkClick: undefined,
      isProtected: false,
    },
    ...(hasSportsTwo
      ? [
          {
            icon: (
              <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
                <LazyLoadImage
                  className="icon-after opacity-0 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
                  alt="Deportes 2"
                  src={deportes_2_after}
                  style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
                />
                <LazyLoadImage
                  className="icon-before opacity-1 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
                  alt="Deportes 2"
                  src={deportes_2_before}
                  style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
                />
              </div>
            ),
            label: `${t('sports')} 2`,
            link: '/sports/bti',
            onLinkClick: undefined,
            isProtected: false,
          },
        ]
      : [
          {
            icon: (
              <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
                <LazyLoadImage
                  className="icon-after opacity-0 absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4"
                  alt="Sponsors"
                  src={
                    theme === 'dark'
                      ? sponsors_dark_after
                      : sponsors_light_after
                  }
                  style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
                />
                <LazyLoadImage
                  className="icon-before opacity-1 absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4"
                  alt="Sponsors"
                  src={
                    theme === 'dark'
                      ? sponsors_dark_before
                      : sponsors_light_before
                  }
                  style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
                />
              </div>
            ),
            label: 'SPONSORS',
            link: '/sponsors',
            onLinkClick: undefined,
            isProtected: false,
          },
        ]),
  ]

  const casinoTabs: TabObj[] = [
    {
      icon: (
        <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
          <LazyLoadImage
            className="icon-after opacity-0 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Deportes 1"
            src={deportes_1_after}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
          <LazyLoadImage
            className="icon-before opacity-1 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Deportes 1 Before"
            src={deportes_1_before}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
        </div>
      ),
      label: t('sports'),
      link: hasSportsTwo && !username ? '/sports/bti' : '/sports/im',
      onLinkClick: undefined,
      isProtected: false,
    },
    {
      icon: (
        <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
          <LazyLoadImage
            className="icon-after opacity-0 absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino Favoritos"
            src={favoritos_after}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
          <LazyLoadImage
            className="icon-before opacity-1 absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino Favoritos"
            src={favoritos_before}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
        </div>
      ),
      label: t('casino:favorites'),
      link: '/casino/favoritos',
      onLinkClick: undefined,
      isProtected: true,
    },
    {
      icon: (
        <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
          <LazyLoadImage
            className="icon-after opacity-0 absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino Recientes"
            src={recently_after}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
          <LazyLoadImage
            className="icon-before opacity-1 absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino Recientes"
            src={recently_before}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
        </div>
      ),
      label: t('recent'),
      link: '/casino/recientes',
      onLinkClick: () => {
        loadMoreGames('recent_played', '', true)
      },
      isProtected: true,
    },
    {
      icon: (
        <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
          <LazyLoadImage
            className="icon-after opacity-0 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino"
            src={casino_after}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
          <LazyLoadImage
            className="icon-before opacity-1 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino"
            src={casino_before}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
        </div>
      ),
      label: t('casino'),
      link: `/casino/inicio`,
      onLinkClick: () => {
        loadMoreGames('15648', '', true)
      },
      isProtected: false,
    },
  ]

  const casinoGameTabs: TabObj[] = [
    {
      icon: (
        <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
          <LazyLoadImage
            className="icon-after opacity-0 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino"
            src={casino_after}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
          <LazyLoadImage
            className="icon-before opacity-1 absolute w-full dark:w-4/5  h-full dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino"
            src={casino_before}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
        </div>
      ),
      label: t('casino'),
      link: `/casino/inicio`,
      onLinkClick: () => {
        loadMoreGames('15648', '', true)
      },
      isProtected: false,
    },
    {
      icon: (
        <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
          <LazyLoadImage
            className="icon-after opacity-0 absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino Recientes"
            src={recently_after}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
          <LazyLoadImage
            className="icon-before opacity-1 absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino Recientes"
            src={recently_before}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
        </div>
      ),
      label: t('recent'),
      link: '/casino/recientes',
      onLinkClick: () => {
        loadMoreGames('recent_played', '', true)
      },
      isProtected: true,
    },
    {
      icon: (
        <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
          <LazyLoadImage
            className="icon-after opacity-0 absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino Favoritos"
            src={favoritos_after}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
          <LazyLoadImage
            className="icon-before opacity-1 absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4"
            alt="Casino Favoritos"
            src={favoritos_before}
            style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
          />
        </div>
      ),
      label: t('casino:favorites'),
      link: '/casino/favoritos',
      onLinkClick: undefined,
      isProtected: true,
    },
    {
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="#888"
          width="24"
          height="24">
          <path
            className="!fill-none"
            stroke-linecap="round"
            stroke-linejoin="round"
            d="m15.75 15.75-2.489-2.489m0 0a3.375 3.375 0 1 0-4.773-4.773 3.375 3.375 0 0 0 4.774 4.774ZM21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
      ),
      label: t('casino:search-category'),
      link: '',
      onLinkClick: () => {
        searchModal.show()
      },
      isProtected: true,
    },
  ]

  const tabs: TabObj[] = useMemo(() => {
    // Game Page
    if (/\/casino\/.+\//.test(fullPath)) return casinoGameTabs
    if (fullPath.includes('/casino/')) return casinoTabs
    if (fullPath.includes('/sports')) return sportTabs
    return commonTabs
  }, [fullPath, casinoGameTabs, casinoTabs, sportTabs, commonTabs])

  return (
    <div
      className={classNames(
        'game-card-glow before:!p-[3px] !fixed z-[120] bg-[rgba(255,255,255,0.75)] dark:bg-[rgba(10,10,10,0.75)] backdrop-blur-md rounded-full w-[calc(100%-32px)]',
        'left-4 grid h-[60px] items-center transition-all duration-[0.2s] ease-[ease-in-out] px-3 py-0',
        'border border-solid border-[color-mix(in_srgb,var(--glowColorSecondary)_25%,transparent)] dark:border-[color-mix(in_srgb,var(--highlight-gold)_35%,transparent)]',
        'shadow-[0px_-2px_5px_color-mix(in_srgb,var(--glowColorSecondary)_15%,transparent)] dark:shadow-[0_0_12px_color-mix(in_srgb,var(--highlight-gold)_30%,transparent)]',
      )}
      style={{
        bottom: isFullScreen ? -60 : 8,
        gridTemplateColumns: `repeat(${tabs.length + 1},1fr)`,
      }}>
      {tabs.map(tab =>
        tab.link ? (
          <Link
            to={tab.isProtected && !username ? undefined : tab.link}
            key={tab.label}
            onClick={tab.onLinkClick}
            search={{}}
            params={{}}>
            <div
              className={classNames(
                'tab flex flex-col items-center relative',
                '[&_svg]:w-[25px] [&_svg]:transition-all [&_svg]:duration-[0.2s] [&_svg]:ease-[ease-in-out]',
                tab.link.includes(`/casino/inicio`)
                  ? fullPath.includes('/casino') &&
                    !fullPath.includes('favoritos') &&
                    !fullPath.includes('recientes')
                    ? '[&_p]:text-highlight-gold [&_svg]:fill-highlight-gold [&_path]:fill-highlight-gold active'
                    : '[&_p]:text-[#888] [&_svg]:fill-[#888] [&_path]:fill-[#888]'
                  : fullPath === tab.link
                    ? '[&_p]:text-highlight-gold [&_svg]:fill-highlight-gold [&_path]:fill-highlight-gold active'
                    : fullPath === tab.link
                      ? '[&_p]:text-highlight-gold [&_svg]:fill-highlight-gold [&_path]:fill-highlight-gold active'
                      : '[&_p]:text-[#888] [&_svg]:fill-[#888] [&_path]:fill-[#888]',
              )}
              onClick={() => {
                if (tab.isProtected && !username) {
                  navigate({ to: '/register' })
                }
              }}>
              {tab.badge}
              <div className="w-[30px] h-[30px] flex justify-center items-center transition-all duration-[0.2s] ease-[ease-in-out] relative rounded-[50%]">
                {tab.icon}
              </div>
              <p className="text-[10px] uppercase text-center">{tab.label}</p>
            </div>
          </Link>
        ) : (
          <button key={tab.label} onClick={tab.onLinkClick}>
            <div
              className={classNames(
                'tab flex flex-col items-center relative',
                '[&_svg]:w-[25px] [&_svg]:transition-all [&_svg]:duration-[0.2s] [&_svg]:ease-[ease-in-out]',
              )}
              onClick={() => {
                if (tab.isProtected && !username) {
                  navigate({ to: '/register' })
                }
              }}>
              <div className="w-[30px] h-[30px] flex justify-center items-center transition-all duration-[0.2s] ease-[ease-in-out] relative rounded-[50%]">
                {tab.icon}
              </div>
              <p className="text-[10px] uppercase text-center">{tab.label}</p>
            </div>
          </button>
        ),
      )}
      <div
        className={classNames(
          `tab flex flex-col items-center w-[calc(100%_/_${tabs.length}_-_24px)] transition-all duration-[0.2s] ease-[ease-in-out] right-3`,
          isFullScreen ? 'absolute bottom-[68px]' : 'bottom-2',
        )}
        onClick={() => setIsFullScreen(!isFullScreen)}>
        <div className="w-[30px] h-[30px] flex justify-center items-center transition-all duration-[0.2s] ease-[ease-in-out] relative rounded-[50%]">
          <img
            className={classNames(
              'w-[25px] transition-all duration-[0.2s] ease-[ease-in-out]',
              isFullScreen && 'w-[40px]',
            )}
            src={isFullScreen ? minimize : maximize}
            alt="hide"
            style={{ rotate: `${isFullScreen ? 0 : 180}deg` }}
          />
        </div>
        {!isFullScreen && (
          <p className="text-[10px] uppercase text-center">{t('expand')}</p>
        )}
      </div>
    </div>
  )
}

export default BottomNav
